/**
 * The percentage field displays whole digits only (the "%" is a separate
 * suffix slot, not part of the text), so these are plain digit<->decimal
 * conversions — no backspace-over-suffix compensation needed.
 */

/**
 * Text shown in the input for a given field value.
 *
 * In `allowEmpty` mode, `null`/`undefined` renders as `""` so the
 * `placeholder` shows through instead of a misleading "0" — a real `0` is
 * a legitimate value and still renders as "0".
 */
export function percentageValueToText(
  value: number | null | undefined,
  allowEmpty: boolean,
): string {
  if (allowEmpty && (value === null || value === undefined)) {
    return "";
  }
  // Math.round avoids float noise (e.g. 0.07 * 100 === 6.999999999999999).
  return String(Math.round((value ?? 0) * 100));
}

/**
 * Parses the raw digits typed into the input into a decimal percentage
 * value (e.g. "15" -> 0.15), or `undefined` when the field is cleared in
 * `allowEmpty` mode.
 */
export function parsePercentageText(
  text: string,
  allowEmpty: boolean,
): number | undefined {
  const digits = text.replace(/\D/g, "");
  if (allowEmpty && digits === "") {
    return undefined;
  }
  const stripped = digits.replace(/^0+(?=\d)/, "") || "0";
  return Number(stripped) / 100;
}
